'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSqlite } from './client/useSqlite';
import { formatSql } from './lib/formatSql';
import { DatabaseSidebar } from './components/DatabaseSidebar';
import { EditorPane } from './components/EditorPane';
import { EmptyState } from './components/EmptyState';
import { ResultsGrid } from './components/ResultsGrid';
import { StatusBar } from './components/StatusBar';
import { Toolbar } from './components/Toolbar';
import type { RemoteDataset } from './samples/catalog';
import { assertSqliteHeader, convertGithubUrl, dbNameFromUrl } from './samples/customUrl';
import { SAMPLE_DB_NAME, SAMPLE_INFO, SAMPLE_SEED_SQL } from './samples/sampleSeed';
import type { ColumnValue, DatabaseInfo, SchemaTable } from './worker/protocol';
import { newId, type EditorTab, type ResultEntry } from './types';

const MAX_RESULT_TABS = 10;
const MAX_IMPORT_BYTES = 256 * 1024 * 1024;
const SQLITE_MAGIC = 'SQLite format 3\0';
const ALLOWED_IMPORT_EXT = /\.(db|sqlite|sqlite3)$/i;

function hasSqliteMagic(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 16) return false;
  const head = new Uint8Array(buf, 0, 16);
  for (let i = 0; i < SQLITE_MAGIC.length; i++) {
    if (head[i] !== SQLITE_MAGIC.charCodeAt(i)) return false;
  }
  return true;
}

const DEFAULT_DB = 'scratch';
const ONBOARDED_KEY = 'yesql:onboarded';
const SPLIT_KEY = 'yesql:editor-split-pct';
const DEFAULT_SPLIT_PCT = 45;
const MIN_SPLIT_PCT = 15;
const MAX_SPLIT_PCT = 85;

function readSplitPct(): number {
  try {
    if (typeof window === 'undefined') return DEFAULT_SPLIT_PCT;
    const raw = window.localStorage.getItem(SPLIT_KEY);
    if (!raw) return DEFAULT_SPLIT_PCT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_SPLIT_PCT;
    return Math.max(MIN_SPLIT_PCT, Math.min(MAX_SPLIT_PCT, n));
  } catch {
    return DEFAULT_SPLIT_PCT;
  }
}
const STARTER_SQL = `-- No way. YesQL — a real SQLite database, running entirely offline in your browser.
-- Persisted via OPFS so your data survives reloads.
-- ⌘/Ctrl+Enter to run the statement at the cursor.

SELECT 'hello, world' AS greeting, datetime('now') AS at;`;

function readOnboardedFlag(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOnboardedFlag(): void {
  try {
    window.localStorage.setItem(ONBOARDED_KEY, '1');
  } catch {
    // Private browsing / quota / disabled storage — silently no-op. The
    // overlay will just keep showing on reload, which is correct: we
    // genuinely can't persist the dismissal.
  }
}

function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function sanitizeName(input: string): string {
  return input
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 64);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function fetchWithProgress(
  url: string,
  onProgress: (loaded: number, total: number | null) => void,
): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`);
  const contentLength = res.headers.get('content-length');
  const total = contentLength ? Number(contentLength) : null;
  if (!res.body) {
    // Browser without ReadableStream support — fall back to a single buffer.
    const buf = await res.arrayBuffer();
    onProgress(buf.byteLength, total);
    return buf;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress(loaded, total);
    }
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out.buffer;
}

function isSelectish(raw: string): boolean {
  // Strip leading comments + whitespace, then peek the first keyword.
  const cleaned = raw
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/--[^\n]*/g, '')
    .trim()
    .toLowerCase();
  return /^(select|with|pragma|explain|values)\b/.test(cleaned);
}

export function QueryRunnerDemo() {
  const { client, status: engine } = useSqlite();

  const initialTabId = useRef(newId('tab'));
  const [tabs, setTabs] = useState<EditorTab[]>(() => [
    { id: initialTabId.current, title: 'Welcome', sql: STARTER_SQL },
  ]);

  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const [editorPct, setEditorPct] = useState<number>(() => readSplitPct());

  useEffect(() => {
    try {
      window.localStorage.setItem(SPLIT_KEY, String(editorPct));
    } catch {
      // ignore quota / disabled storage
    }
  }, [editorPct]);

  useEffect(() => {
    return () => {
      try {
        window.localStorage.removeItem(SPLIT_KEY);
      } catch {
        // ignore
      }
    };
  }, []);

  const onSplitterPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      const y = ev.clientY - rect.top;
      const pct = (y / rect.height) * 100;
      setEditorPct(Math.max(MIN_SPLIT_PCT, Math.min(MAX_SPLIT_PCT, pct)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, []);
  const [activeTabId, setActiveTabId] = useState<string>(initialTabId.current);
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0] ?? null;
  const sql = activeTab?.sql ?? '';
  const setSql = useCallback(
    (next: string) => {
      setTabs((prev) => prev.map((t) => (t.id === activeTabId ? { ...t, sql: next } : t)));
    },
    [activeTabId],
  );

  const newTab = useCallback((seed?: { title?: string; sql?: string }) => {
    const id = newId('tab');
    setTabs((prev) => {
      const n = prev.length + 1;
      return [
        ...prev,
        {
          id,
          title: seed?.title ?? `Query ${n}`,
          sql: seed?.sql ?? '',
        },
      ];
    });
    setActiveTabId(id);
    return id;
  }, []);

  const closeTab = useCallback(
    (id: string) => {
      setTabs((prev) => {
        if (prev.length <= 1) {
          // Don't let the strip go fully empty — reset to a single fresh tab.
          const fresh: EditorTab = { id: newId('tab'), title: 'Query', sql: '' };
          setActiveTabId(fresh.id);
          return [fresh];
        }
        const idx = prev.findIndex((t) => t.id === id);
        if (idx < 0) return prev;
        const next = prev.filter((t) => t.id !== id);
        if (activeTabId === id) {
          const fallback = next[idx] ?? next[idx - 1] ?? next[0];
          if (fallback) setActiveTabId(fallback.id);
        }
        return next;
      });
    },
    [activeTabId],
  );

  const renameTab = useCallback((id: string, title: string) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === id ? { ...t, title: title.trim() || t.title } : t)),
    );
  }, []);

  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ResultEntry[]>([]);
  const [activeResultId, setActiveResultId] = useState<string | null>(null);
  /** Transient status-bar message for non-query operations (open db, import,
   *  download progress, etc). Per-query messages live on the ResultEntry. */
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const activeResult = results.find((r) => r.id === activeResultId) ?? null;

  const setMessage = setStatusMessage;
  const setError = useCallback((msg: string | null) => {
    // Loose "I want to surface an error to the user" calls (DB switch
    // failure, etc.) — convert into a synthetic result entry so the
    // failure has a tab and isn't lost on the next run.
    if (!msg) return;
    const id = newId('res');
    setResults((prev) => {
      const next = [
        ...prev,
        {
          id,
          sql: '',
          pending: false,
          result: null,
          error: msg,
          message: null,
          tookMs: null,
          ts: Date.now(),
        },
      ];
      return next.length > MAX_RESULT_TABS ? next.slice(next.length - MAX_RESULT_TABS) : next;
    });
    setActiveResultId(id);
  }, []);

  const closeResult = useCallback(
    (id: string) => {
      setResults((prev) => {
        const idx = prev.findIndex((r) => r.id === id);
        if (idx < 0) return prev;
        const next = prev.filter((r) => r.id !== id);
        if (activeResultId === id) {
          // Pick a sibling: prefer the one that took its place, else the
          // previous tab, else nothing.
          const fallback = next[idx] ?? next[idx - 1] ?? null;
          setActiveResultId(fallback?.id ?? null);
        }
        return next;
      });
    },
    [activeResultId],
  );

  const closeAllResults = useCallback(() => {
    setResults([]);
    setActiveResultId(null);
  }, []);

  const [history, setHistory] = useState<string[]>([]);
  // The Get Started overlay is one-time-ever onboarding, persisted in
  // localStorage. Initial value reads the flag synchronously so we don't
  // flash the overlay on second-and-later opens.
  const [hasInteracted, setHasInteractedRaw] = useState<boolean>(readOnboardedFlag);
  const setHasInteracted = useCallback((v: boolean) => {
    setHasInteractedRaw(v);
    if (v) writeOnboardedFlag();
  }, []);
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [activeDb, setActiveDb] = useState<string | null>(null);
  const [schema, setSchema] = useState<SchemaTable[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);

  const refreshDatabases = useCallback(async () => {
    if (!client) return;
    try {
      const list = await client.listDatabases();
      setDatabases(list);
    } catch {
      // Worker not reachable; ignore — engine status will show the failure.
    }
  }, [client]);

  const refreshSchema = useCallback(async () => {
    if (!client) return;
    setSchemaLoading(true);
    try {
      const next = await client.schema();
      setSchema(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSchemaLoading(false);
    }
  }, [client, setError]);

  const switchTo = useCallback(
    async (name: string) => {
      if (!client || !name) return;
      try {
        const info = await client.open(name);
        setActiveDb(info.name);
        setMessage(`Opened "${info.name}".`);
        await refreshSchema();
        await refreshDatabases();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [client, refreshDatabases, refreshSchema, setError, setMessage],
  );

  // External-system effect: bootstrap the engine session — open the default
  // database and pull the initial database list + schema. The worker is the
  // external system; we observe its results once after init.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (engine.phase !== 'ready' || !client || bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const info = await client.open(DEFAULT_DB);
        if (cancelled) return;
        setActiveDb(info.name);
        await refreshDatabases();
        await refreshSchema();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, engine.phase, refreshDatabases, refreshSchema, setError]);

  const runSql = useCallback(
    async (raw: string) => {
      if (!client || engine.phase !== 'ready') return;
      setHasInteracted(true);
      setBusy(true);
      setStatusMessage(null);
      setHistory((prev) => {
        // Ring-buffer style: drop duplicate-of-most-recent, cap at 50.
        const trimmed = raw.trim();
        if (!trimmed) return prev;
        const next = prev[prev.length - 1] === trimmed ? prev : [...prev, trimmed];
        return next.length > 50 ? next.slice(next.length - 50) : next;
      });
      // Add a pending entry up front so the user gets a tab to focus
      // immediately. We patch it in place when the run completes.
      const id = newId('res');
      const placeholder: ResultEntry = {
        id,
        sql: raw,
        pending: true,
        result: null,
        error: null,
        message: null,
        tookMs: null,
        ts: Date.now(),
      };
      setResults((prev) => {
        const next = [...prev, placeholder];
        return next.length > MAX_RESULT_TABS ? next.slice(next.length - MAX_RESULT_TABS) : next;
      });
      setActiveResultId(id);
      const start = performance.now();
      let mutated = false;
      const patch = (changes: Partial<ResultEntry>) => {
        setResults((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));
      };
      try {
        if (isSelectish(raw)) {
          const r = await client.select(raw);
          patch({
            pending: false,
            result: r,
            message: `Returned ${r.rows.length} row(s)${r.truncated ? ' (truncated)' : ''}.`,
            tookMs: performance.now() - start,
          });
        } else {
          const r = await client.exec(raw);
          mutated = true;
          patch({
            pending: false,
            message:
              `Executed ${r.statementCount} statement(s) · ${r.totalChanges} change(s)` +
              (r.lastInsertRowid != null ? ` · last rowid ${r.lastInsertRowid}` : ''),
            tookMs: performance.now() - start,
          });
        }
        if (mutated) {
          await refreshSchema();
          await refreshDatabases();
        }
      } catch (err) {
        patch({
          pending: false,
          error: err instanceof Error ? err.message : String(err),
          tookMs: performance.now() - start,
        });
      } finally {
        setBusy(false);
      }
    },
    [client, engine.phase, refreshSchema, refreshDatabases, setHasInteracted],
  );

  const createDatabase = useCallback(
    async (name: string) => {
      if (!client) return;
      // Opening a new name in the SAH pool creates the file on first use.
      await switchTo(name);
    },
    [client, switchTo],
  );

  const deleteDatabase = useCallback(
    async (name: string) => {
      if (!client) return;
      try {
        await client.deleteDatabase(name);
        setMessage(`Deleted "${name}".`);
        const list = await client.listDatabases();
        setDatabases(list);
        if (activeDb === name) {
          // Pick a sibling if one exists; otherwise reopen the default.
          const fallback = list[0]?.name ?? DEFAULT_DB;
          await switchTo(fallback);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [client, activeDb, switchTo, setError, setMessage],
  );

  const insertSnippet = useCallback(
    (text: string) => {
      // Append after a space — without DOM access to the textarea cursor we
      // can't truly "insert at cursor" cross-tab. A space + snippet on the
      // active tab is good enough for the schema-picker workflow.
      const current = activeTab?.sql ?? '';
      const sep = current && !/\s$/.test(current) ? ' ' : '';
      setSql(current + sep + text);
    },
    [activeTab, setSql],
  );

  const selectFromTable = useCallback(
    (name: string) => {
      const sqlText = `SELECT * FROM ${quoteIdent(name)} LIMIT 100;`;
      const existing = tabs.find((t) => t.title === name && t.sql === sqlText);
      if (existing) {
        setActiveTabId(existing.id);
      } else {
        newTab({ title: name, sql: sqlText });
      }
      runSql(sqlText);
    },
    [tabs, newTab, runSql],
  );

  const countTable = useCallback(
    (name: string) => {
      const sqlText = `SELECT count(*) FROM ${quoteIdent(name)};`;
      newTab({ title: `count(${name})`, sql: sqlText });
      runSql(sqlText);
    },
    [newTab, runSql],
  );

  const showDdl = useCallback((table: SchemaTable) => {
    const id = newId('res');
    setResults((prev) => {
      const next = [
        ...prev,
        {
          id,
          sql: `-- DDL for ${table.name}`,
          pending: false,
          result: {
            columns: ['ddl'],
            rows: [[table.ddl] as ColumnValue[]],
            truncated: false,
          },
          error: null,
          message: null,
          tookMs: null,
          ts: Date.now(),
        },
      ];
      return next.length > MAX_RESULT_TABS ? next.slice(next.length - MAX_RESULT_TABS) : next;
    });
    setActiveResultId(id);
  }, []);

  const dropTable = useCallback(
    (name: string) => {
      if (!confirm(`Drop table "${name}"? This cannot be undone.`)) return;
      runSql(`DROP TABLE ${quoteIdent(name)};`);
    },
    [runSql],
  );

  const formatText = useCallback(
    async (text: string): Promise<string | null> => {
      const trimmed = text.trim();
      if (!trimmed) return text;
      try {
        return await formatSql(text);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setMessage(`Format failed: ${msg}`);
        return null;
      }
    },
    [setMessage],
  );

  const exportActive = useCallback(async () => {
    if (!client || !activeDb) return;
    try {
      const { name, bytes } = await client.exportDatabase();
      downloadBlob(new Blob([bytes], { type: 'application/x-sqlite3' }), `${name}.db`);
      setMessage(`Exported "${name}" (${bytes.byteLength.toLocaleString()} bytes).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [client, activeDb, setError, setMessage]);

  const exportDb = useCallback(
    async (name: string) => {
      if (!client) return;
      // exportDatabase() always exports the active connection — switch first
      // when the requested DB isn't already active.
      try {
        if (activeDb !== name) await client.open(name);
        const { bytes } = await client.exportDatabase();
        downloadBlob(new Blob([bytes], { type: 'application/x-sqlite3' }), `${name}.db`);
        setMessage(`Exported "${name}" (${bytes.byteLength.toLocaleString()} bytes).`);
        if (activeDb !== name && activeDb) {
          // Restore the previously-active connection so we don't surprise
          // the user with a context switch they didn't ask for.
          await client.open(activeDb);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [client, activeDb, setError, setMessage],
  );

  const renameDb = useCallback(
    async (oldName: string, newName: string) => {
      if (!client) return;
      try {
        await client.renameDatabase(oldName, newName);
        await refreshDatabases();
        if (activeDb === oldName) setActiveDb(newName);
        setMessage(`Renamed "${oldName}" → "${newName}".`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [activeDb, client, refreshDatabases, setError, setMessage],
  );

  const importFile = useCallback(
    async (file: File) => {
      if (!client) return;
      setHasInteracted(true);
      try {
        if (file.size === 0) {
          throw new Error(`"${file.name}" is empty.`);
        }
        if (file.size > MAX_IMPORT_BYTES) {
          throw new Error(
            `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB — max ${MAX_IMPORT_BYTES / 1024 / 1024} MB.`,
          );
        }
        if (!ALLOWED_IMPORT_EXT.test(file.name)) {
          throw new Error(
            `"${file.name}" doesn't look like a SQLite file (.db / .sqlite / .sqlite3).`,
          );
        }
        const buf = await file.arrayBuffer();
        if (!hasSqliteMagic(buf)) {
          throw new Error(`"${file.name}" isn't a valid SQLite database (bad header).`);
        }
        const baseName =
          sanitizeName(file.name.replace(/\.(db|sqlite|sqlite3)$/i, '')) || 'imported';
        // Avoid clobbering an existing database — append (1), (2)... if needed.
        const existingNames = new Set(databases.map((d) => d.name));
        let candidate = baseName;
        let n = 1;
        while (existingNames.has(candidate)) {
          candidate = `${baseName}_${n++}`;
        }
        await client.importDatabase(candidate, buf);
        await refreshDatabases();
        await switchTo(candidate);
        setMessage(
          `Imported "${file.name}" as "${candidate}" (${buf.byteLength.toLocaleString()} bytes).`,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [client, databases, refreshDatabases, switchTo, setError, setHasInteracted, setMessage],
  );

  const sampleExists = databases.some((d) => d.name === SAMPLE_DB_NAME);
  const [remoteBusy, setRemoteBusy] = useState(false);

  const fetchCustomUrl = useCallback(
    async (rawUrl: string) => {
      if (!client || remoteBusy) return;
      const url = convertGithubUrl(rawUrl);
      if (!/^https?:\/\//i.test(url)) {
        setError('URL must start with http:// or https://');
        return;
      }
      setHasInteracted(true);
      setRemoteBusy(true);
      setError(null);
      const label = dbNameFromUrl(url);
      setMessage(`Downloading ${label}…`);
      try {
        const buf = await fetchWithProgress(url, (loaded, total) => {
          if (total) {
            setMessage(`Downloading ${label}… ${Math.round((loaded / total) * 100)}%`);
          } else {
            setMessage(`Downloading ${label}… ${(loaded / 1024 / 1024).toFixed(1)} MB`);
          }
        });
        assertSqliteHeader(buf);
        const existingNames = new Set(databases.map((d) => d.name));
        let target = label;
        let n = 1;
        while (existingNames.has(target)) target = `${label}_${n++}`;
        await client.importDatabase(target, buf);
        await refreshDatabases();
        await switchTo(target);
        setMessage(
          `Imported ${label} as "${target}" (${(buf.byteLength / 1024 / 1024).toFixed(2)} MB).`,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // Most failures here are CORS — the message is the only signal we
        // get, so explicitly point users at the likely cause.
        setError(
          /failed to fetch|networkerror|cors/i.test(msg)
            ? `Couldn't fetch ${url} — the server probably doesn't allow cross-origin downloads. raw.githubusercontent.com always works; many other hosts don't.`
            : msg,
        );
      } finally {
        setRemoteBusy(false);
      }
    },
    [
      client,
      databases,
      refreshDatabases,
      remoteBusy,
      switchTo,
      setError,
      setHasInteracted,
      setMessage,
    ],
  );

  const fetchRemoteDataset = useCallback(
    async (dataset: RemoteDataset) => {
      if (!client || remoteBusy) return;
      setHasInteracted(true);
      setRemoteBusy(true);
      setError(null);
      setMessage(`Downloading ${dataset.name}…`);
      try {
        const buf = await fetchWithProgress(dataset.url, (loaded, total) => {
          if (total) {
            const pct = Math.round((loaded / total) * 100);
            setMessage(`Downloading ${dataset.name}… ${pct}%`);
          } else {
            setMessage(`Downloading ${dataset.name}… ${(loaded / 1024 / 1024).toFixed(1)} MB`);
          }
        });
        assertSqliteHeader(buf);
        const existingNames = new Set(databases.map((d) => d.name));
        let target = dataset.dbName;
        let n = 1;
        while (existingNames.has(target)) target = `${dataset.dbName}_${n++}`;
        await client.importDatabase(target, buf);
        await refreshDatabases();
        await switchTo(target);
        setMessage(
          `Imported ${dataset.name} as "${target}" (${(buf.byteLength / 1024 / 1024).toFixed(2)} MB).`,
        );
      } catch (err) {
        setError(
          err instanceof Error ? `Couldn't fetch ${dataset.name}: ${err.message}` : String(err),
        );
      } finally {
        setRemoteBusy(false);
      }
    },
    [
      client,
      databases,
      refreshDatabases,
      remoteBusy,
      switchTo,
      setError,
      setHasInteracted,
      setMessage,
    ],
  );

  const loadSample = useCallback(async () => {
    if (!client) return;
    setHasInteracted(true);
    try {
      if (sampleExists) {
        // Already created — just switch to it instead of re-seeding so the
        // user's edits to the sample DB aren't silently destroyed.
        await switchTo(SAMPLE_DB_NAME);
        setSql(SAMPLE_INFO.starterQuery);
        return;
      }
      await client.open(SAMPLE_DB_NAME);
      await client.exec(SAMPLE_SEED_SQL);
      setActiveDb(SAMPLE_DB_NAME);
      setSql(SAMPLE_INFO.starterQuery);
      setMessage(`Loaded sample database into "${SAMPLE_DB_NAME}".`);
      await refreshSchema();
      await refreshDatabases();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [
    client,
    sampleExists,
    refreshDatabases,
    refreshSchema,
    switchTo,
    setError,
    setHasInteracted,
    setMessage,
    setSql,
  ]);

  const canManage = engine.phase === 'ready' && engine.info.vfs === 'opfs-sahpool';

  // External-system effect: subscribe to viewport width via matchMedia so we
  // can collapse the sidebar into an overlay on small windows. matchMedia is
  // an external system; React just observes its current state.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 720px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Auto-close the overlay sidebar after the user picks a DB or table on mobile.
  const wrapHandler = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void) =>
      (...args: Args) => {
        fn(...args);
        if (isNarrow) setSidebarOpen(false);
      },
    [isNarrow],
  );

  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const isFileDrag = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!isFileDrag(e) || !canManage) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current += 1;
      setDragOver(true);
    },
    [canManage],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!isFileDrag(e) || !canManage) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    },
    [canManage],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setDragOver(false);
      if (!canManage) {
        setError("Import isn't available in ephemeral mode.");
        return;
      }
      const items = e.dataTransfer.items;
      if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const entry = items[i]?.webkitGetAsEntry?.();
          if (entry?.isDirectory) {
            setError("Folders aren't supported — drop a single SQLite file.");
            return;
          }
        }
      }
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (e.dataTransfer.files.length > 1) {
        setError('Drop one SQLite file at a time.');
        return;
      }
      void importFile(file);
    },
    [canManage, importFile, setError],
  );

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden bg-[#2b2b2b] text-[#bbbbbb]"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-[#323232] bg-[#3c3f41] px-2 py-1">
        <div className="flex min-w-0 items-center gap-2">
          {isNarrow && (
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              className="rounded border border-[#4a4a4a] bg-[#2b2b2b] px-2 py-1 text-[11px] text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
              aria-label="Toggle databases"
            >
              ☰
            </button>
          )}
          <span className="hidden truncate text-[12px] text-[#808080] sm:inline">
            No way. A real SQLite database, running entirely offline in your browser.
          </span>
        </div>
        <div className="flex items-center gap-2">
          {engine.phase === 'ready' && engine.info.vfs === 'memory' && (
            <span className="rounded border border-[#7a6a2a]/50 bg-[#3a3424] px-2 py-0.5 text-[11px] text-[#bbb529]">
              Ephemeral — OPFS unavailable
            </span>
          )}
          <Toolbar
            canExport={canManage && !!activeDb}
            canImport={canManage}
            canSample={engine.phase === 'ready'}
            sampleExists={sampleExists}
            remoteBusy={remoteBusy}
            onExport={exportActive}
            onImport={importFile}
            onLoadSample={loadSample}
            onPickRemote={fetchRemoteDataset}
            onCustomUrl={fetchCustomUrl}
          />
        </div>
      </header>

      <div className="relative flex flex-1 overflow-hidden">
        {!isNarrow && (
          <div className="w-56 shrink-0">
            <DatabaseSidebar
              databases={databases}
              activeDb={activeDb}
              schema={schema}
              schemaLoading={schemaLoading}
              canManage={canManage}
              onSwitch={switchTo}
              onCreate={createDatabase}
              onRename={renameDb}
              onDelete={deleteDatabase}
              onExportDb={exportDb}
              onRefresh={refreshSchema}
              onPickTable={selectFromTable}
              onSelectFromTable={selectFromTable}
              onCountTable={countTable}
              onShowDdl={showDdl}
              onDropTable={dropTable}
              onInsertSnippet={insertSnippet}
            />
          </div>
        )}
        {isNarrow && sidebarOpen && (
          <>
            <div
              className="absolute inset-0 z-20 bg-black/60"
              onClick={() => setSidebarOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute inset-y-0 left-0 z-30 w-[80%] max-w-[280px] shadow-2xl">
              <DatabaseSidebar
                databases={databases}
                activeDb={activeDb}
                schema={schema}
                schemaLoading={schemaLoading}
                canManage={canManage}
                onSwitch={wrapHandler(switchTo)}
                onCreate={wrapHandler(createDatabase)}
                onRename={renameDb}
                onDelete={wrapHandler(deleteDatabase)}
                onExportDb={wrapHandler(exportDb)}
                onRefresh={refreshSchema}
                onPickTable={wrapHandler(selectFromTable)}
                onSelectFromTable={wrapHandler(selectFromTable)}
                onCountTable={wrapHandler(countTable)}
                onShowDdl={wrapHandler(showDdl)}
                onDropTable={wrapHandler(dropTable)}
                onInsertSnippet={wrapHandler(insertSnippet)}
              />
            </div>
          </>
        )}
        <div ref={splitContainerRef} className="relative flex flex-1 flex-col overflow-hidden">
          <div
            className="flex min-h-[80px] flex-col overflow-hidden"
            style={{ height: `${editorPct}%` }}
          >
            <EditorPane
              tabs={tabs}
              activeTabId={activeTabId}
              onSelectTab={setActiveTabId}
              onNewTab={() => newTab()}
              onCloseTab={closeTab}
              onRenameTab={renameTab}
              onCloseOtherTabs={(keepId) => {
                setTabs((prev) => prev.filter((t) => t.id === keepId));
                setActiveTabId(keepId);
              }}
              onCloseAllTabs={() => {
                const fresh = newId('tab');
                setTabs([{ id: fresh, title: 'Query', sql: '' }]);
                setActiveTabId(fresh);
              }}
              value={sql}
              onChange={setSql}
              onRun={runSql}
              onFormat={formatText}
              busy={busy}
              disabled={engine.phase !== 'ready'}
              history={history}
            />
          </div>
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize editor / results"
            onPointerDown={onSplitterPointerDown}
            onDoubleClick={() => setEditorPct(DEFAULT_SPLIT_PCT)}
            className="group relative h-1 shrink-0 cursor-ns-resize bg-[#323232] hover:bg-[#5394ec]"
            title="Drag to resize · double-click to reset"
          >
            <div className="absolute inset-x-0 -top-1 -bottom-1" />
          </div>
          <div className="flex min-h-[80px] flex-1 overflow-hidden">
            <ResultsGrid
              results={results}
              activeResultId={activeResultId}
              busy={busy}
              onSelect={setActiveResultId}
              onClose={closeResult}
              onCloseAll={closeAllResults}
            />
          </div>
          {!hasInteracted && engine.phase === 'ready' && (
            <EmptyState
              sampleExists={sampleExists}
              canManage={canManage}
              remoteBusy={remoteBusy}
              onLoadSample={loadSample}
              onImport={importFile}
              onPickRemote={fetchRemoteDataset}
              onCustomUrl={fetchCustomUrl}
              onDismiss={() => setHasInteracted(true)}
            />
          )}
        </div>
      </div>

      <StatusBar
        engine={engine}
        activeDb={activeDb}
        activeDbBytes={databases.find((d) => d.name === activeDb)?.byteLength ?? null}
        databaseCount={databases.length}
        lastTookMs={activeResult?.tookMs ?? null}
        rowCount={activeResult?.result?.rows.length ?? null}
        message={statusMessage}
      />
      {dragOver && (
        <div
          className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-[#2b2b2b]/85 backdrop-blur-sm"
          aria-hidden="true"
        >
          <div className="rounded-lg border-2 border-dashed border-[#499c54] bg-[#3c3f41] px-6 py-4 text-center shadow-2xl">
            <div className="text-[14px] font-semibold text-[#e8e8e8]">
              {canManage ? 'Drop a SQLite file to import' : 'Import unavailable in ephemeral mode'}
            </div>
            <div className="mt-1 text-[11px] text-[#808080]">
              .db / .sqlite / .sqlite3 · max {MAX_IMPORT_BYTES / 1024 / 1024} MB
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
